import { ReactElement } from 'react';
import { AUX_TIME_OFFSET_LIMIT_S, movingAverageOptions } from './auxSensorChannels';
import { AUX_ACCENT } from './auxPlotStyle';

const ACCENT_STYLE = { accentColor: AUX_ACCENT };
const RADIO_STYLE = { accentColor: AUX_ACCENT, width: '16px', height: '16px' };
const NUMBER_CLASS = 'cr-w-20 cr-rounded cr-border cr-border-gray-300 cr-px-2 cr-py-1 '
  + 'cr-text-right cr-tabular-nums';

/** Radio row shared by the mode toggles and the moving-average selector. */
export function ModeRadio({
  name,
  value,
  options,
  onChange,
}: {
  name: string;
  value: string;
  options: { value: string; label: string }[];
  onChange: (value: string) => void;
}): ReactElement {
  return (
    <div className="cr-flex cr-gap-4">
      {options.map((o) => (
        <label key={o.value} className="cr-flex cr-cursor-pointer cr-items-center cr-gap-2">
          <input
            type="radio"
            name={name}
            value={o.value}
            checked={value === o.value}
            onChange={() => onChange(o.value)}
            className="cr-cursor-pointer"
            style={RADIO_STYLE}
          />
          <span className="cr-text-sm cr-font-medium">{o.label}</span>
        </label>
      ))}
    </div>
  );
}

/** Moving-average width selector, labelled by span in seconds. */
export function MovingAverageControl({
  name,
  window,
  onChange,
  intervalS,
}: {
  name: string;
  window: number;
  onChange: (window: number) => void;
  intervalS: number;
}): ReactElement {
  return (
    <div className="cr-mb-3 cr-flex cr-flex-wrap cr-items-center cr-gap-x-4 cr-gap-y-2 cr-text-sm">
      <span className="cr-whitespace-nowrap cr-font-medium cr-text-gray-600">Moving average</span>
      <ModeRadio
        name={name}
        value={String(window)}
        options={movingAverageOptions(intervalS)}
        onChange={(v) => onChange(parseInt(v, 10))}
      />
    </div>
  );
}

/**
 * Time-offset control for the auxiliary instances of a sensor type: a ±2 s
 * slider stepping in whole grid samples, a typed entry for exact values, and a
 * reset. `appliedS` is the offset actually in force after grid snapping — shown
 * only when it differs from what was asked for.
 */
export function AuxTimeOffsetControl({
  requestS,
  onChange,
  appliedS,
  stepS,
}: {
  requestS: number;
  onChange: (seconds: number) => void;
  appliedS: number;
  stepS: number;
}): ReactElement {
  const clamp = (v: number) => Math.max(-AUX_TIME_OFFSET_LIMIT_S, Math.min(AUX_TIME_OFFSET_LIMIT_S, v));
  const snapped = Math.abs(appliedS - requestS) > 1e-6;

  return (
    <div className="cr-mb-3 cr-flex cr-flex-wrap cr-items-center cr-gap-x-3 cr-gap-y-2 cr-text-sm">
      <span className="cr-whitespace-nowrap cr-font-medium cr-text-gray-600">
        Aux time offset <span className="cr-text-gray-400">(meters 2+)</span>
      </span>
      <input
        type="range"
        min={-AUX_TIME_OFFSET_LIMIT_S}
        max={AUX_TIME_OFFSET_LIMIT_S}
        step={stepS}
        value={requestS}
        onChange={(e) => onChange(parseFloat(e.target.value))}
        style={ACCENT_STYLE}
        className="cr-min-w-[180px] cr-flex-1 cr-cursor-pointer"
        aria-label="Auxiliary sensor time offset"
      />
      <input
        type="number"
        min={-AUX_TIME_OFFSET_LIMIT_S}
        max={AUX_TIME_OFFSET_LIMIT_S}
        step={stepS}
        value={requestS}
        onChange={(e) => {
          const parsed = parseFloat(e.target.value);
          onChange(Number.isNaN(parsed) ? 0 : clamp(parsed));
        }}
        aria-label="Auxiliary sensor time offset (seconds)"
        className={NUMBER_CLASS}
      />
      <span className="cr-whitespace-nowrap cr-text-gray-500">
        s
        {snapped ? (
          <span className="cr-pl-2 cr-text-gray-400">{`→ applied ${appliedS.toFixed(2)} s`}</span>
        ) : null}
      </span>
      <button
        type="button"
        onClick={() => onChange(0)}
        disabled={requestS === 0}
        className="cr-rounded cr-border cr-border-gray-300 cr-px-2 cr-py-1 cr-text-xs cr-text-gray-600
          hover:cr-bg-gray-100 disabled:cr-opacity-40"
      >
        Reset
      </button>
    </div>
  );
}

/** Min-speed threshold shared by the slip-ratio plots. */
export function MinSpeedControl({
  value,
  onChange,
  maxMs,
}: {
  value: number;
  onChange: (value: number) => void;
  /** Upper bound, already resolved by the caller (run max, or a fallback). */
  maxMs: number;
}): ReactElement {
  return (
    <div className="cr-flex cr-items-center cr-gap-3 cr-rounded-lg cr-bg-white cr-p-4 cr-text-sm cr-shadow-md">
      <span className="cr-whitespace-nowrap cr-font-medium cr-text-gray-600">
        Min speed <span className="cr-text-gray-400">(slip-ratio plots)</span>
      </span>
      <input
        type="range"
        min={0}
        max={maxMs}
        step={0.5}
        value={value}
        onChange={(e) => onChange(parseFloat(e.target.value))}
        style={ACCENT_STYLE}
        className="cr-flex-1 cr-cursor-pointer"
        aria-label="Minimum speed"
      />
      <span className="cr-w-16 cr-whitespace-nowrap cr-tabular-nums cr-text-gray-700">
        {value.toFixed(1)} m/s
      </span>
    </div>
  );
}
